// @file Composable for defining keyboard shortcuts and their effects.
import device from '@@/bits/device'
import { isActiveElementAnInputField } from '@@/bits/dom'
import type OzInput from '@@/library/v4/components/OzInput.vue'
import tinykeys from 'tinykeys'
import type { Ref } from 'vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'

interface Shortcut {
  key: string
  callback: (event: KeyboardEvent) => void
  triggerOnce?: boolean
  clearCache?: boolean
  skipIfActiveElementIsInputField?: boolean
}

export const useKeyboardShortcuts = (shortcuts: Shortcut[]): void => {
  if (device.mobile) return

  let removeKeyboardShortcuts: (() => void) | null = null
  const shortcutTriggerRecord = ref<Record<string, boolean>>({}) // tracking for shortcuts to be only triggered once

  onMounted(() => {
    const keyboardShortcuts = shortcuts.reduce<Record<string, (event: KeyboardEvent) => void>>(
      (acc, { key, callback, triggerOnce = false, clearCache = false, skipIfActiveElementIsInputField = false }) => {
        acc[key] = (event: KeyboardEvent) => {
          // For shortcuts that should skip if the active element is an input field, return early to prevent callback execution
          // e.g. for `/` shortcut, we don't want to focus the search input if another text input is already focused
          if (skipIfActiveElementIsInputField && isActiveElementAnInputField()) return

          // For shorcuts that should only trigger once, we don't want to trigger the callback if the same shortcut is pressed again.
          if (triggerOnce) {
            if (shortcutTriggerRecord.value[key]) {
              // The shortcut is being triggered for the second time. Reset the trigger record, skip the callback.
              shortcutTriggerRecord.value[key] = false
              return
            }
            shortcutTriggerRecord.value[key] = true
          }

          if (clearCache) {
            shortcutTriggerRecord.value = {}
          }

          callback(event)
        }
        return acc
      },
      {},
    )

    removeKeyboardShortcuts = tinykeys(document.documentElement, keyboardShortcuts)
  })

  onBeforeUnmount(() => {
    removeKeyboardShortcuts?.()
  })
}

export const useKeyboardShortcutsForSearch = (
  searchInput: Ref<InstanceType<typeof OzInput> | null | undefined>,
): void => {
  useKeyboardShortcuts([
    {
      key: '/',
      callback: (event: KeyboardEvent) => {
        const isSearchInputFocused = searchInput.value?.$el.contains(document.activeElement) ?? false
        if (!isSearchInputFocused) {
          event.preventDefault()
          searchInput.value?.focusInput()
        }
      },
      triggerOnce: true,
      skipIfActiveElementIsInputField: true,
    },
    {
      key: '$mod+/',
      callback: (event: KeyboardEvent) => {
        event.preventDefault()
        searchInput.value?.focusInput()
      },
      triggerOnce: true,
    },
    {
      key: '$mod+KeyF',
      callback: (event: KeyboardEvent) => {
        event.preventDefault()
        searchInput.value?.focusInput()
      },
      triggerOnce: true,
    },
    {
      key: 'Escape',
      callback: () => {
        searchInput.value?.blurInput()
      },
      clearCache: true,
    },
  ])
}

export const useKeyboardShortcutsForUndoRedo = ({
  handleUndo,
  handleRedo,
}: {
  handleUndo: (event: KeyboardEvent) => Promise<void>
  handleRedo: (event: KeyboardEvent) => Promise<void>
}): void => {
  useKeyboardShortcuts([
    {
      key: '$mod+KeyZ',
      callback: (event: KeyboardEvent) => {
        void handleUndo(event)
      },
    },
    {
      key: device.windows ? '$mod+KeyY' : 'Shift+$mod+KeyZ',
      callback: (event: KeyboardEvent) => {
        void handleRedo(event)
      },
    },
  ])
}
